import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Vehicles from "@/pages/Vehicles";
import Drivers from "@/pages/Drivers";
import Bookings from "@/pages/Bookings";
import BookingForm from "@/pages/BookingForm";
import BookingDetail from "@/pages/BookingDetail";
import PrintDoc from "@/pages/PrintDoc";
import Finance from "@/pages/Finance";
import UsersPage from "@/pages/Users";
import CompanySettings from "@/pages/CompanySettings";
import Platform from "@/pages/Platform";
import Mileage from "@/pages/Mileage";
import PodRecords from "@/pages/PodRecords";
import MarketVendors from "@/pages/MarketVendors";
import MarketTrips from "@/pages/MarketTrips";
import MarketTripDetail from "@/pages/MarketTripDetail";
import MarketReports from "@/pages/MarketReports";
import VendorStatement from "@/pages/VendorStatement";
import PartyBookings from "@/pages/PartyBookings";
import Tds from "@/pages/Tds";
import PerKmReport from "@/pages/PerKmReport";
import IdleDays from "@/pages/IdleDays";
import DriverApp from "@/pages/DriverApp";
import DriverTrackApp from "@/pages/DriverTrackApp";
import ProfitLoss from "@/pages/ProfitLoss";
import BulkUpload from "@/pages/BulkUpload";
import CentralExpenses from "@/pages/CentralExpenses";
import Purchases from "@/pages/Purchases";
import BankRecon from "@/pages/BankRecon";
import GstReturns from "@/pages/GstReturns";
import GstLedger from "@/pages/GstLedger";
import SelectCompany from "@/pages/SelectCompany";
import Parties from "@/pages/Parties";
import PartyProfile from "@/pages/PartyProfile";
import DriverProfile from "@/pages/DriverProfile";
import DriverHome from "@/pages/DriverHome";
import DriverTrip from "@/pages/DriverTrip";
import StaffHR from "@/pages/StaffHR";
import Loans from "@/pages/Loans";
import Tracking from "@/pages/Tracking";
import Fastag from "@/pages/Fastag";
import Diesel from "@/pages/Diesel";
import { UploadProgress } from "@/components/UploadProgress";

function Loader() {
  return <div className="flex min-h-screen items-center justify-center text-sm text-muted-foreground" data-testid="app-loading">Loading…</div>;
}

function Guard({ roles }) {
  const { user, role } = useAuth();
  if (user === null) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(role)) return <Navigate to="/" replace />;
  return <Outlet />;
}

function Home() {
  const { user, role } = useAuth();
  if (user === null) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  if (role === "super_admin") return <Navigate to="/platform" replace />;
  if (role === "driver") return <Navigate to="/driver" replace />;
  if (role === "operation") return <Navigate to="/bookings" replace />;
  return <Navigate to="/dashboard" replace />;
}

const OPS = ["admin", "manager", "operation", "accounts"];

export default function App() {
  return (
    <div className="App">
      <ThemeProvider>
        <AuthProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/track" element={<DriverTrackApp />} />
              <Route path="/track/:token" element={<DriverTrackApp />} />
              <Route path="/select-company" element={<SelectCompany />} />
              <Route path="/" element={<Home />} />
              <Route element={<Guard roles={OPS} />}>
                <Route path="/print/:kind/:id" element={<PrintDoc />} />
                <Route element={<Layout />}>
                  <Route path="/drivers" element={<Drivers />} />
                  <Route path="/drivers/:id" element={<DriverProfile />} />
                  <Route path="/party-bookings" element={<PartyBookings />} />
                  <Route path="/bulk-lr" element={<BulkUpload />} />
                  <Route path="/tds" element={<Tds />} />
                  <Route path="/per-km-report" element={<PerKmReport />} />
                  <Route path="/idle-days" element={<IdleDays />} />
                  <Route path="/driver-app" element={<DriverApp />} />
                  <Route path="/profit-loss" element={<ProfitLoss />} />
                  <Route path="/profit-loss/:tab" element={<ProfitLoss />} />
                  <Route path="/bookings" element={<Bookings />} />
                  <Route path="/bookings/new" element={<BookingForm />} />
                  <Route path="/bookings/:id" element={<BookingDetail />} />
                  <Route path="/bookings/:id/edit" element={<BookingForm />} />
                  <Route path="/market" element={<Navigate to="/market/trips" replace />} />
                  <Route path="/market/vendors" element={<MarketVendors />} />
                  <Route path="/market/trips" element={<MarketTrips />} />
                  <Route path="/market/trips/:id" element={<MarketTripDetail />} />
                </Route>
              </Route>
              <Route element={<Guard roles={["admin", "manager", "operation"]} />}>
                <Route element={<Layout />}>
                  <Route path="/vehicles" element={<Vehicles />} />
                </Route>
              </Route>
              <Route element={<Guard roles={["admin", "manager", "operation", "accounts"]} />}>
                <Route element={<Layout />}>
                  <Route path="/parties" element={<Parties />} />
                  <Route path="/diesel" element={<Diesel />} />
                </Route>
              </Route>
              <Route element={<Guard roles={["admin", "manager", "accounts"]} />}>
                <Route element={<Layout />}>
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/parties/:id" element={<PartyProfile />} />
                  <Route path="/pod-records" element={<PodRecords />} />
                  <Route path="/market/reports" element={<MarketReports />} />
                  <Route path="/central-expenses" element={<CentralExpenses />} />
                  <Route path="/purchases" element={<Purchases />} />
                  <Route path="/suppliers" element={<Purchases initialTab="suppliers" />} />
                  <Route path="/bank" element={<BankRecon />} />
                  <Route path="/gst" element={<GstReturns />} />
                  <Route path="/gst-ledger" element={<GstLedger />} />
                  <Route path="/market/vendors/:id/statement" element={<VendorStatement />} />
                  <Route path="/finance" element={<Finance />} />
                  <Route path="/loans" element={<Loans />} />
                  <Route path="/tracking" element={<Tracking />} />
                  <Route path="/fastag" element={<Fastag />} />
                  <Route path="/hr" element={<StaffHR />} />
                  <Route path="/hr/:tab" element={<StaffHR />} />
                </Route>
              </Route>
              <Route element={<Guard roles={["admin", "manager"]} />}>
                <Route element={<Layout />}>
                  <Route path="/mileage" element={<Mileage />} />
                </Route>
              </Route>
              <Route element={<Guard roles={["admin"]} />}>
                <Route element={<Layout />}>
                  <Route path="/users" element={<UsersPage />} />
                  <Route path="/settings" element={<CompanySettings />} />
                </Route>
              </Route>
              <Route element={<Guard roles={["super_admin"]} />}>
                <Route element={<Layout />}>
                  <Route path="/platform" element={<Platform />} />
                </Route>
              </Route>
              <Route element={<Guard roles={["driver"]} />}>
                <Route path="/driver" element={<DriverHome />} />
                <Route path="/driver/trip/:id" element={<DriverTrip />} />
              </Route>
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
          <Toaster richColors position="top-right" />
          <UploadProgress />
        </AuthProvider>
      </ThemeProvider>
    </div>
  );
}
